import { supabase } from '../lib/supabase';
import { MediaFile, FileType } from '../types/media';

export const getMediaFiles = async (factoryId: string): Promise<MediaFile[]> => {
  try {
    const { data, error } = await supabase
      .from('media')
      .select('*')
      .eq('factory_id', factoryId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching media files:', error);
      throw new Error('Échec du chargement des fichiers. Veuillez réessayer.');
    }

    return data.map(file => ({
      id: file.id,
      name: file.name,
      type: file.type,
      size: file.size,
      url: file.url,
      path: file.path,
      factoryId: file.factory_id,
      userId: file.user_id,
      createdAt: new Date(file.created_at).getTime(),
      fileType: file.file_type,
      description: file.description,
      bucketId: file.bucket_id
    })) || [];
  } catch (error) {
    console.error('Get media files error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Une erreur est survenue lors du chargement des fichiers.');
  }
};

export const uploadFile = async (
  file: File, 
  factoryId: string,
  fileType: FileType,
  onProgress: (progress: number) => void
): Promise<string> => {
  try {
    // Validate file type
    const allowedTypes = [
      'video/mp4',
      'audio/mpeg'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Type de fichier non supporté. Seuls les fichiers MP3 et MP4 sont acceptés.');
    }

    // Validate file size (500MB max)
    if (file.size > 524288000) {
      throw new Error('Le fichier est trop volumineux. La taille maximale est de 500MB.');
    }

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Vous devez être connecté pour télécharger des fichiers.');
    }

    // Create unique file path
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${sanitizedFileName}`;
    const filePath = `${user.id}/${factoryId}/${fileName}`;

    // Initial progress
    onProgress(0);

    // Upload options
    const options = {
      cacheControl: '3600',
      contentType: file.type,
      upsert: false
    };

    // Upload with retry
    let retries = 3;
    let uploadResult = null;

    while (retries > 0) {
      const { data, error } = await supabase.storage
        .from('media')
        .upload(filePath, file, options);

      if (!error) {
        uploadResult = data;
        break;
      }

      console.warn(`Upload attempt failed (${retries} retries left):`, error);
      retries--;
      
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, (3 - retries) * 1000));
      } else {
        throw error;
      }
    }

    if (!uploadResult) {
      throw new Error('Échec du téléchargement après plusieurs tentatives.');
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('media')
      .getPublicUrl(uploadResult.path);

    // Add to database
    const { error: dbError } = await supabase
      .from('media')
      .insert({
        name: file.name,
        type: file.type,
        size: file.size,
        url: publicUrl,
        path: uploadResult.path,
        factory_id: factoryId,
        user_id: user.id,
        file_type: fileType,
        bucket_id: 'media'
      });

    if (dbError) {
      // Cleanup failed upload
      await supabase.storage
        .from('media')
        .remove([uploadResult.path]);
      throw dbError;
    }

    onProgress(100);
    return publicUrl;

  } catch (error) {
    console.error('Upload error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Échec du téléchargement. Veuillez réessayer.');
  }
};

export const deleteFile = async (file: MediaFile): Promise<void> => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Vous devez être connecté pour supprimer des fichiers.');
    }

    // Verify user owns the file
    const { data: mediaFile, error: mediaError } = await supabase
      .from('media')
      .select('user_id')
      .eq('id', file.id)
      .single();

    if (mediaError || !mediaFile) {
      throw new Error('Fichier non trouvé.');
    }

    if (mediaFile.user_id !== user.id) {
      throw new Error('Vous n\'avez pas la permission de supprimer ce fichier.');
    }

    // Delete file from storage
    const { error: storageError } = await supabase.storage
      .from('media')
      .remove([file.path]);

    if (storageError) {
      throw new Error(`Erreur lors de la suppression du fichier: ${storageError.message}`);
    }

    // Delete record from database
    const { error: dbError } = await supabase
      .from('media')
      .delete()
      .eq('id', file.id);

    if (dbError) {
      throw new Error(`Erreur lors de la suppression de l'enregistrement: ${dbError.message}`);
    }

  } catch (error) {
    console.error('Delete error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Échec de la suppression. Veuillez réessayer.');
  }
};

export const updateFileName = async (fileId: string, newName: string): Promise<void> => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Vous devez être connecté pour modifier des fichiers.');
    }

    // Verify user owns the file
    const { data: mediaFile, error: mediaError } = await supabase
      .from('media')
      .select('user_id')
      .eq('id', fileId)
      .single();

    if (mediaError || !mediaFile) {
      throw new Error('Fichier non trouvé.');
    }

    if (mediaFile.user_id !== user.id) {
      throw new Error('Vous n\'avez pas la permission de modifier ce fichier.');
    }

    // Update file name in database
    const { error: dbError } = await supabase
      .from('media')
      .update({ name: newName.trim() })
      .eq('id', fileId)
      .eq('user_id', user.id);

    if (dbError) {
      throw new Error(`Erreur lors de la mise à jour du nom: ${dbError.message}`);
    }
  } catch (error) {
    console.error('Update name error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Une erreur est survenue lors de la mise à jour du nom.');
  }
};

export const updateFileDate = async (fileId: string, newDate: Date): Promise<void> => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Vous devez être connecté pour modifier des fichiers.');
    }

    // Verify user owns the file
    const { data: mediaFile, error: mediaError } = await supabase
      .from('media')
      .select('user_id')
      .eq('id', fileId)
      .single();

    if (mediaError || !mediaFile) {
      throw new Error('Fichier non trouvé.');
    }

    if (mediaFile.user_id !== user.id) {
      throw new Error('Vous n\'avez pas la permission de modifier ce fichier.');
    }

    // Update file date in database
    const { error: dbError } = await supabase
      .from('media')
      .update({ created_at: newDate.toISOString() })
      .eq('id', fileId)
      .eq('user_id', user.id);

    if (dbError) {
      throw new Error(`Erreur lors de la mise à jour de la date: ${dbError.message}`);
    }
  } catch (error) {
    console.error('Update date error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Une erreur est survenue lors de la mise à jour de la date.');
  }
};

export const updateFileDescription = async (fileId: string, description: string): Promise<void> => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Vous devez être connecté pour modifier des fichiers.');
    }

    // Verify user owns the file
    const { data: mediaFile, error: mediaError } = await supabase
      .from('media')
      .select('user_id')
      .eq('id', fileId)
      .single();

    if (mediaError || !mediaFile) {
      throw new Error('Fichier non trouvé.');
    }

    if (mediaFile.user_id !== user.id) {
      throw new Error('Vous n\'avez pas la permission de modifier ce fichier.');
    }

    // Validate description length
    if (description.length > 1000) {
      throw new Error('La description ne peut pas dépasser 1000 caractères.');
    }

    // Update file description in database
    const { error: dbError } = await supabase
      .from('media')
      .update({ description: description.trim() || null })
      .eq('id', fileId)
      .eq('user_id', user.id);

    if (dbError) {
      throw new Error(`Erreur lors de la mise à jour de la description: ${dbError.message}`);
    }
  } catch (error) {
    console.error('Update description error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Une erreur est survenue lors de la mise à jour de la description.');
  }
};